import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { useApi } from '../../lib/api';

type Me = {
  user_id: string;
  email: string;
  role: 'buyer' | 'seller' | 'admin';
  seller_status: 'none' | 'pending' | 'approved' | 'rejected' | 'suspended';
  stripe_account_id: string | null;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  connect_details_submitted: boolean;
};

export default function SellerDashboard() {
  const api = useApi();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const me = await api<Me>('/me');
      setMe(me);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'error');
    }
  }
  useEffect(() => {
    refresh();
  }, []);

  async function startStripe() {
    setLoading(true);
    try {
      const { url } = await api<{ url: string }>('/sellers/stripe/onboard', { method: 'POST' });
      window.location.href = url;
    } finally {
      setLoading(false);
    }
  }

  const approved = me?.seller_status === 'approved';
  const stripeStarted = !!me?.stripe_account_id;
  const stripeReady = !!me?.payouts_enabled;

  return (
    <section>
      <h2>Seller dashboard</h2>
      <SignedOut>
        <SignInButton mode="modal" />
      </SignedOut>
      <SignedIn>
        {err && <p style={{ color: 'crimson' }}>{err}</p>}
        {me && (
          <div className="card" style={{ marginBottom: '1rem' }}>
            <p>
              Signed in as <strong>{me.email}</strong>
            </p>
            <p>
              Seller status: <span className="badge">{me.seller_status}</span> · Stripe:{' '}
              <span className="badge">{stripeReady ? 'connected' : 'not connected'}</span>
            </p>
            {!approved && me.seller_status === 'none' && (
              <p>
                <Link to="/seller/apply" className="btn">
                  Apply to sell
                </Link>
              </p>
            )}
            {me.seller_status === 'pending' && (
              <p className="muted">Your application is under review (usually within 48 hours).</p>
            )}
            {me.seller_status === 'rejected' && (
              <p className="muted">Application not approved. Contact support if you believe this is in error.</p>
            )}
          </div>
        )}

        <div className="grid cols-3">
          <div className="card">
            <h3>Payouts</h3>
            <p className="muted">
              {stripeReady
                ? 'Your Stripe payouts are active.'
                : stripeStarted
                  ? 'Finish your Stripe setup to start listing — payouts aren’t active yet.'
                  : 'Connect your bank or debit card via Stripe to receive payouts.'}
            </p>
            <button onClick={startStripe} disabled={loading}>
              {loading
                ? 'Redirecting…'
                : stripeReady
                  ? 'Manage Stripe account'
                  : stripeStarted
                    ? 'Finish Stripe setup'
                    : 'Connect with Stripe'}
            </button>
            {approved && !stripeReady && (
              <p className="muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                You can’t create listings until payouts are active.
              </p>
            )}
          </div>
          <Link to="/seller/seat-blocks" className="card">
            <h3>Seat blocks</h3>
            <p className="muted">Submit seats for verification.</p>
          </Link>
          <Link to="/seller/listings" className="card">
            <h3>My listings</h3>
            <p className="muted">Create, edit, and cancel listings.</p>
          </Link>
          <Link to="/seller/sales" className="card">
            <h3>My sales</h3>
            <p className="muted">View buyers and mark transfers.</p>
          </Link>
        </div>
      </SignedIn>
    </section>
  );
}
